/* eslint-disable react-hooks/exhaustive-deps */
import { Dashboard, DashboardType } from '@eagle/core-data-types';
import { DetailPage, Icon, useAuthenticated, useCustomRoutes } from '@eagle/react-common';
import { Breadcrumbs, Stack, Typography } from '@ethings/ui-toolkit';
import { Box, Link } from '@mui/material';
import { FC, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useParams } from 'react-router-dom';
import { DashboardDetailCard } from './dashboard-detail-card';

interface Props {
  pageType: DashboardType;
}

export const DASHBOARD_DETAIL_FLAG = 'track-dashboard-detail-feature';

export const DashboardDetail: FC<Props> = ({ pageType: initialPageType }) => {
  const { t } = useTranslation(['track']);
  const { restClient } = useAuthenticated();
  const { dashboardId } = useParams();
  const { dashboards, reports } = useCustomRoutes();
  const dashboardsHref = useHref(`/${dashboards}`);
  const reportsHref = useHref(`/${reports}`);

  const [pageType, setPageType] = useState(initialPageType);

  const renderPageContent = (data: Dashboard): JSX.Element => <>
    <Stack
      direction="column"
      flex={[2, 2]}
      spacing={2}
      minWidth={0}
    >
      <DashboardDetailCard data={data} setPageType={setPageType} />
    </Stack>

    <Stack
      direction="column"
      flex={[3, 3]}
      spacing={2}
    />
  </>;

  const breadcrumbs = (
    <Breadcrumbs>
      <Link
        color="inherit"
        href={pageType === DashboardType.DASHBOARD ? dashboardsHref : reportsHref}
        underline="hover"
      >
        {t(`track:page.dashboard-detail.${pageType}.sub-title`)}
      </Link>
      <Typography color="text.primary" data-testid="person-detail-subtitle-breadcrumb">{t('track:page.dashboard-detail.dashboard-details.sub-title')}</Typography>
    </Breadcrumbs>
  );

  const loadData = useCallback(
    async () => {
      if (!dashboardId) return;
      return restClient.dashboard.get(dashboardId);
    },
    [dashboardId, restClient]
  );

  return (
    <Box sx={{
      display: 'flex',
      flex: [1, 1],
      flexDirection: 'column',
      minWidth: 0,
    }}>
      <DetailPage<Dashboard>
        data-testid='dashboard-detail'
        actions={[]}
        breadcrumbs={breadcrumbs}
        loadData={loadData}
        pageIcon={<Icon icon="DashboardIcon" />}
        renderDisplay={({ display }) => display}
        renderPageContent={renderPageContent}
        renderPageTitle={(data: Dashboard) => data.display}
      />
    </Box>
  );
};
